import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { useCart } from "../contexts/CartContext";
import logoEmblem from "../assets/logo-emblem-dark.png";
import NotificationBell from "./NotificationBell";
import FavoritesNavIcon from "./FavoritesNavIcon";
import HeaderNavPanel from "./HeaderNavPanel";
import AccountMenu from "./AccountMenu";
import CurrencySwitcher from "./CurrencySwitcher";

/**
 * Rolex-inspired minimal header:
 *   [☰] .......... [emblem] .......... [🛒] [♡] [🔔] [👤]
 *
 * Every text nav link (including "Home Page") lives inside <HeaderNavPanel>.
 * The top bar shows only icons:
 *   left   — hamburger (opens the drawer)
 *   centre — the emblem alone (no wordmark), always links home
 *   right  — basket + favorites + notifications + account dropdown
 */
export default function Header() {
  const { user } = useAuth();
  const { t } = useLang();
  const { count: cartCount } = useCart();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const iconBtn = "w-9 h-9 flex items-center justify-center rounded-full border border-white/15 hover:border-gold hover:text-gold transition-colors";

  return (
    <>
      <header className="sticky top-0 z-50 glass-strong border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 grid grid-cols-[1fr_auto_1fr] items-center">
          {/* Left — hamburger only (Home Page label moved into the drawer) */}
          <div className="flex justify-start items-center">
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              data-testid="header-menu-btn"
              className={iconBtn}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
          </div>

          {/* Centre — emblem only, no text */}
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center justify-center"
            aria-label={t("brand")}
          >
            <img
              src={logoEmblem}
              alt={t("brand")}
              data-testid="brand-logo"
              className="h-12 w-12 object-contain drop-shadow-[0_0_10px_rgba(212,175,55,0.35)]"
            />
          </Link>

          {/* Right — essential icons only */}
          <div className="flex justify-end items-center gap-2 md:gap-3">
            <CurrencySwitcher compact />
            <button
              onClick={() => nav(user ? "/cart" : "/login")}
              aria-label="Shopping basket"
              data-testid="header-cart-btn"
              className={`${iconBtn} relative`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {user && cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-black text-[10px] leading-[18px] font-medium text-center"
                  data-testid="header-cart-count"
                >
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </button>
            {user && <FavoritesNavIcon />}
            {user && <NotificationBell />}
            {user ? (
              <AccountMenu iconBtnClass={iconBtn} />
            ) : (
              <button
                onClick={() => nav("/login")}
                aria-label="Sign in"
                data-testid="header-signin-icon"
                className={iconBtn}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </header>

      <HeaderNavPanel open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
